import { FMSType, LocationVarKey } from "@swivl-mono/types";
import { useEffect, useMemo } from "react";

import { WorksheetColumn, WorksheetLayout, WorksheetRow } from "../../../Components/Worksheets/Layout/Worksheet.Layout.Components";
import { IF_ALL, IF_ANY, IS_EQUAL, IS_NOT_EQUAL, PageKey } from "../../../Components/Worksheets/Types/Worksheet.Interface";
import { WorksheetComponent, WorksheetComponentType } from "../../../Components/Worksheets/WorksheetComponent/WorksheetComponent.Component";
import { DropdownRow, Section } from "../../../Components/Worksheets/WorksheetComponent/WorksheetComponents";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import FMSModel from "../../../Models/FMS/FMS.Model";

export const PAGE_KEYS: PageKey[] = [
  LocationVarKey.HasSwivlCRM,
  LocationVarKey.HasLiveChat,
  LocationVarKey.HasCustomCTAMessage,
  LocationVarKey.HasAfterNavigate,
  { key: LocationVarKey.HasShowAllStores, hideIf: IS_EQUAL(LocationVarKey.HasSingleLocation, "1") },
  { key: LocationVarKey.HasInventoryCarousel, hideIf: IS_NOT_EQUAL(LocationVarKey.HasFMS, "1") },
  { key: LocationVarKey.GoLocalFID, hideIf: IS_NOT_EQUAL(LocationVarKey.WebsiteProvider, "golocal") },
  LocationVarKey.HasFMS,
  { key: LocationVarKey.FMSIntegrationId, hideIf: IS_NOT_EQUAL(LocationVarKey.HasFMS, "1") },
  { key: LocationVarKey.FMSId, hideIf: IS_NOT_EQUAL(LocationVarKey.HasFMS, "1") },
  LocationVarKey.FMSSyncEnabled,
  { key: LocationVarKey.HasGateCodeIntegration, hideIf: IS_NOT_EQUAL(LocationVarKey.HasFMS, "1") },
  {
    key: LocationVarKey.HasUnitNumberIntegration,
    hideIf: IF_ANY([IS_NOT_EQUAL(LocationVarKey.HasFMS, "1"), IS_EQUAL(LocationVarKey.FMSType, FMSType.DoorSwap)])
  },
  { key: LocationVarKey.HasAccountBalanceIntegration, hideIf: IS_NOT_EQUAL(LocationVarKey.HasFMS, "1") },
  {
    key: LocationVarKey.HasAccountNumberIntegration,
    hideIf: IF_ANY([IS_NOT_EQUAL(LocationVarKey.HasFMS, "1"), IS_NOT_EQUAL(LocationVarKey.FMSType, FMSType.StorEdge)])
  },
  {
    key: LocationVarKey.HasFMSPlus,
    hideIf: IF_ALL([IS_NOT_EQUAL(LocationVarKey.whichTier, "Innovator"), IS_NOT_EQUAL(LocationVarKey.whichTier, "Disruptor")])
  },
  {
    key: LocationVarKey.HasGoogleReviewIntegration,
    hideIf: IF_ALL([IS_NOT_EQUAL(LocationVarKey.whichTier, "Innovator"), IS_NOT_EQUAL(LocationVarKey.whichTier, "Disruptor")])
  }
];

export default function LocationSubpageInformation() {
  const page = usePropertiesGetLocationIds();
  const { FMSs } = FMSModel.useState();
  useEffect(() => {
    FMSModel.load(false);
  }, []);
  const FMSOptions = useMemo(() => {
    if (!FMSs) return undefined;
    return FMSs.map((a) => ({ label: a.name, value: String(a.id) }));
  }, [FMSs]);

  return (
    <WorksheetLayout>
      <h4>Foundation Features</h4>
      <WorksheetRow>
        <WorksheetColumn>
          <WorksheetComponent componentType={WorksheetComponentType.SWITCH} {...page} label="swivl CRM" keyName={LocationVarKey.HasSwivlCRM} />
          <WorksheetComponent componentType={WorksheetComponentType.SWITCH} {...page} label="Live Chat" keyName={LocationVarKey.HasLiveChat} />
          <WorksheetComponent
            componentType={WorksheetComponentType.SWITCH}
            {...page}
            label="Custom CTA Messages"
            keyName={LocationVarKey.HasCustomCTAMessage}
          />
          <WorksheetComponent
            componentType={WorksheetComponentType.SWITCH}
            {...page}
            label="After Navigate Messages"
            keyName={LocationVarKey.HasAfterNavigate}
          />
          <WorksheetComponent
            componentType={WorksheetComponentType.SWITCH}
            {...page}
            label="Don't require postal code in store lookup"
            hideIf={IS_EQUAL(LocationVarKey.HasSingleLocation, "1")}
            keyName={LocationVarKey.HasShowAllStores}
          />
          <WorksheetComponent
            componentType={WorksheetComponentType.SWITCH}
            {...page}
            label="Inventory Carousel"
            hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")}
            keyName={LocationVarKey.HasInventoryCarousel}
          />
          <WorksheetComponent
            componentType={WorksheetComponentType.SINGLE_LINE}
            {...page}
            hideIf={IS_NOT_EQUAL(LocationVarKey.WebsiteProvider, "golocal")}
            label="GoLocal FID"
            keyName={LocationVarKey.GoLocalFID}
          />
          <DropdownRow
            {...page}
            label="Response Length"
            keyName={LocationVarKey.ResponseSentenceLimit}
            options={[
              {
                label: "Concise (1 sentence)",
                value: "1"
              },
              {
                label: "Brief (1-2 sentences)",
                value: "2"
              },
              {
                label: "Balanced (1-3 sentences)",
                value: "3"
              },
              {
                label: "Detailed (1-4 sentences)",
                value: "4"
              }
            ]}
          />
        </WorksheetColumn>

        <WorksheetColumn>
          <WorksheetComponent
            componentType={WorksheetComponentType.SWITCH}
            {...page}
            label="FMS Integration Enabled"
            keyName={LocationVarKey.HasFMS}
          />
          <WorksheetComponent
            componentType={WorksheetComponentType.DROPDOWN}
            {...page}
            options={FMSOptions}
            label="FMS Integration"
            hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")}
            keyName={LocationVarKey.FMSIntegrationId}
          />
          <WorksheetComponent
            componentType={WorksheetComponentType.SINGLE_LINE}
            {...page}
            label="FMS ID"
            hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")}
            keyName={LocationVarKey.FMSId}
          />

          <WorksheetComponent
            componentType={WorksheetComponentType.SWITCH}
            {...page}
            label="FMS Sync Enabled"
            keyName={LocationVarKey.FMSSyncEnabled}
          />

          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.whichTier, "Engage")}>
            <WorksheetComponent
              componentType={WorksheetComponentType.SWITCH}
              {...page}
              label="Gate Code Integration"
              hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")}
              keyName={LocationVarKey.HasGateCodeIntegration}
            />
            <WorksheetComponent
              componentType={WorksheetComponentType.SWITCH}
              {...page}
              label="Unit Number Integration"
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.HasFMS, "1"), IS_EQUAL(LocationVarKey.FMSType, FMSType.DoorSwap)])}
              keyName={LocationVarKey.HasUnitNumberIntegration}
            />
            <WorksheetComponent
              componentType={WorksheetComponentType.SWITCH}
              {...page}
              label="Account Balance Integration"
              hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")}
              keyName={LocationVarKey.HasAccountBalanceIntegration}
            />
            <WorksheetComponent
              componentType={WorksheetComponentType.SWITCH}
              {...page}
              label="Account Number Integration"
              hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.HasFMS, "1"), IS_NOT_EQUAL(LocationVarKey.FMSType, FMSType.StorEdge)])}
              keyName={LocationVarKey.HasAccountNumberIntegration}
            />
          </Section>
        </WorksheetColumn>
      </WorksheetRow>

      <Section {...page} hideIf={IF_ALL([IS_NOT_EQUAL(LocationVarKey.whichTier, "Innovator"), IS_NOT_EQUAL(LocationVarKey.whichTier, "Disruptor")])}>
        <h4>Innovator Features</h4>
        <WorksheetRow>
          <WorksheetColumn>
            <WorksheetComponent
              componentType={WorksheetComponentType.SWITCH}
              {...page}
              label="Sequences (FMS+)"
              keyName={LocationVarKey.HasFMSPlus}
            />
            <WorksheetComponent
              componentType={WorksheetComponentType.SWITCH}
              {...page}
              label="Google Review Integration"
              keyName={LocationVarKey.HasGoogleReviewIntegration}
            />
          </WorksheetColumn>
          <WorksheetColumn>
            {/* <WorksheetComponent
            componentType={WorksheetComponentType.SWITCH}
            {...page}
            label="DaVinci Code Integration"
            hideIf={IS_NOT_EQUAL(LocationVarKey.FMSType, FMSType.StorEdge)}
            keyName={LocationVarKey.HasDaVinciCodeIntegration}
          /> */}
          </WorksheetColumn>
        </WorksheetRow>
      </Section>
    </WorksheetLayout>
  );
}
